import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ChatSearchMessage } from '@flock-types/chat.types';
import useChatQuickSearch from '@/hooks/useChatQuickSearch';
import useChatSearch from '@/hooks/useChatSearch';
import { formatMessageTime } from '@/lib/chatTime';

interface Props {
  groupId: string;
  chatGroupId: string | null;
  /** chatGroupId is the THREAD THE MESSAGE LIVES IN (Everyone-scope AI search
   * returns matches from all threads in the group, mobile parity). */
  onJumpTo: (messageId: string, chatGroupId: string | null) => void;
  onClose: () => void;
}

function ResultRow({
  message,
  onJump,
}: {
  message: ChatSearchMessage;
  onJump: (id: string, chatGroupId: string | null) => void;
}) {
  const targetId = message.firestoreId || message.id;
  return (
    <button
      onClick={() => onJump(targetId, message.chatGroupId ?? null)}
      className="block w-full rounded-lg px-3 py-2 text-left hover:bg-flock-navy-50"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-flock-navy-700">{message.userName}</span>
        <span className="shrink-0 text-[10px] text-flock-navy-400">
          {formatMessageTime(new Date(message.createdAt))}
        </span>
      </div>
      <div className="mt-0.5 line-clamp-2 text-xs text-flock-navy-600">{message.contentText}</div>
      {message.chatGroupName && (
        <div className="mt-0.5 text-[10px] text-flock-navy-400">in {message.chatGroupName}</div>
      )}
    </button>
  );
}

/**
 * Conversation search: instant quick-search plus AI search ("Ask AI") with
 * a generated answer and source messages. Jumping uses the message-context
 * endpoint via useGroupChat's scroll-context machinery.
 */
export default function ChatSearchPopover({ groupId, chatGroupId, onJumpTo, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'quick' | 'ai'>('quick');
  const quick = useChatQuickSearch(groupId, chatGroupId);
  const ai = useChatSearch(groupId, chatGroupId);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Debounced quick search as you type
  useEffect(() => {
    if (mode !== 'quick') return;
    if (!query.trim()) {
      quick.clear();
      return;
    }
    const timer = setTimeout(() => quick.search(query), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, mode]);

  const handleJump = (id: string, messageChatGroupId: string | null) => {
    onJumpTo(id, messageChatGroupId);
    onClose();
  };

  const activeResult = mode === 'quick' ? quick.result?.messages : ai.result?.messages;
  const activeLoading = mode === 'quick' ? quick.loading : ai.loading;
  const activeError = mode === 'quick' ? quick.error : ai.error;

  return (
    <div
      ref={panelRef}
      className="absolute right-4 top-14 z-40 flex max-h-[70vh] w-96 flex-col rounded-xl border border-flock-gray-200 bg-white shadow-2xl"
    >
      <div className="flex items-center gap-2 border-b border-flock-gray-200 p-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && mode === 'ai') ai.search(query);
            }}
            placeholder={mode === 'quick' ? 'Search messages' : 'Ask about this conversation…'}
            className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-flock-navy-400"
          />
        </div>
        <button onClick={onClose} className="p-1.5 text-flock-navy-400 hover:text-flock-navy-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex gap-1 border-b border-flock-gray-200 px-2 py-1.5">
        <button
          onClick={() => setMode('quick')}
          className={`rounded-full px-3 py-1 text-xs font-medium ${mode === 'quick' ? 'bg-flock-navy-600 text-white' : 'text-flock-navy-500 hover:bg-flock-navy-50'}`}
        >
          Quick search
        </button>
        <button
          onClick={() => {
            setMode('ai');
            if (query.trim()) ai.search(query);
          }}
          className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${mode === 'ai' ? 'bg-flock-navy-600 text-white' : 'text-flock-navy-500 hover:bg-flock-navy-50'}`}
        >
          <SparklesIcon className="h-3.5 w-3.5" /> Ask AI
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {mode === 'ai' && !ai.result && !ai.loading && !ai.error && (
          <div className="p-3 text-xs text-flock-navy-400">
            Describe what you&apos;re looking for — &ldquo;the verse someone shared about
            patience&rdquo; — and press Enter.
            {ai.recentSearches.length > 0 && (
              <div className="mt-3">
                <div className="mb-1 font-semibold uppercase tracking-wide">Recent</div>
                {ai.recentSearches.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setQuery(s);
                      ai.search(s);
                    }}
                    className="block w-full rounded px-2 py-1 text-left text-xs text-flock-navy-600 hover:bg-flock-navy-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {activeLoading && (
          <div className="p-4 text-center text-sm text-flock-navy-400">
            {mode === 'ai' ? 'Thinking…' : 'Searching…'}
          </div>
        )}
        {activeError && <div className="p-4 text-center text-sm text-red-600">{activeError}</div>}

        {mode === 'ai' && ai.result?.answer && (
          <div className="mb-2 rounded-lg bg-flock-navy-50 px-3 py-2 text-xs text-flock-navy-800">
            {ai.result.answer}
          </div>
        )}

        {activeResult && activeResult.length === 0 && !activeLoading && (
          <div className="p-4 text-center text-sm text-flock-navy-400">No messages found.</div>
        )}

        {activeResult?.map((m) => (
          <ResultRow key={m.archiveId || m.firestoreId || m.id} message={m} onJump={handleJump} />
        ))}
      </div>
    </div>
  );
}
